import { useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Copy, CheckCircle } from "lucide-react";
import { CryptoIcon } from "../components/icons/CryptoIcon";

const ASSETS = [
  {
    code: "USDT",
    name: "Tether USD",
    networks: [
      { id: "TRC20", label: "USDT (TRC-20)", subtitle: "Tron", fee: "1 USDT", time: "~1-3 мин" },
    ],
  },
  { code: "TON", name: "Toncoin", networks: [{ id: "TON", label: "TON", subtitle: "TON Network", fee: "0.01 TON", time: "~1 мин" }] },
  { code: "TRX", name: "Tron", networks: [{ id: "TRX", label: "TRX", subtitle: "Tron", fee: "1 TRX", time: "~1 мин" }] },
  { code: "BNB", name: "BNB", networks: [{ id: "BNB", label: "BNB", subtitle: "BNB Smart Chain", fee: "0.0005 BNB", time: "~3 мин" }] },
];

const DEFAULT_ADDRESSES = {
  USDT: "TVry9c6bqVZTCwF3XsUfvzwwJKhJsHtMQp",
  TON: "UQAk2K9q3rEZl3oZkRf5wMy9qXMqKQzgwSt9Iv5e7rQc8x5W",
  TRX: "TVry9c6bqVZTCwF3XsUfvzwwJKhJsHtMQp",
  BNB: "0x55d398326f99059ff775485246999027b3197955",
};

const Deposit = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const initialAsset = searchParams.get("asset") || "USDT";
  const [activeAsset, setActiveAsset] = useState(
    ASSETS.find((a) => a.code === initialAsset) ? initialAsset : "USDT"
  );
  const [activeNetworkIdx, setActiveNetworkIdx] = useState(0);
  const [copied, setCopied] = useState(false);

  const asset = useMemo(() => ASSETS.find((a) => a.code === activeAsset) || ASSETS[0], [activeAsset]);
  const network = asset.networks[activeNetworkIdx] || asset.networks[0];

  // Address resolution: admin-set per-asset > admin-set USDT wallet_address > default fallback
  const address = useMemo(() => {
    const userAddresses = user?.asset_addresses || {};
    if (asset.code === "USDT") {
      return user?.wallet_address || DEFAULT_ADDRESSES.USDT;
    }
    return userAddresses[asset.code] || DEFAULT_ADDRESSES[asset.code];
  }, [user, asset]);

  const totalBalance = (user?.balance || 0) + (user?.trading_balance || 0);

  const handleCopy = () => {
    navigator.clipboard.writeText(address);
    setCopied(true);
    toast.success("Адрес скопирован!");
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <div className="min-h-screen bybit-grid-subtle">
      {/* Header */}
      <header className="border-b border-[#1F1F1F] bg-[#000000]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/wallet" className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors">
              <ArrowLeft size={20} />
              <span>Назад</span>
            </Link>
            <h1 className="font-chivo font-bold text-xl text-white">Web-<span className="text-[#EDEDED]">3</span></h1>
            <div className="flex items-center gap-2 bg-[#0A0A0A] px-4 py-2 rounded border border-[#1F1F1F]">
              <span className="text-[#A1A1AA] text-sm">Общий:</span>
              <span className="font-mono text-white font-bold">$ {totalBalance.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-[#0A0A0A] rounded-xl border border-[#1F1F1F] p-6 mb-6">
          <h2 className="font-chivo font-bold text-xl text-white mb-2">Пополнение</h2>
          <p className="text-[#A1A1AA] text-sm mb-6">Выберите актив и сеть, скопируйте адрес для пополнения.</p>

          {/* Asset selector */}
          <div className="mb-6">
            <div className="text-[#A1A1AA] text-sm mb-2">Актив</div>
            <div className="grid grid-cols-4 gap-2">
              {ASSETS.map((a) => {
                const active = a.code === activeAsset;
                return (
                  <button
                    key={a.code}
                    type="button"
                    onClick={() => {
                      setActiveAsset(a.code);
                      setActiveNetworkIdx(0);
                    }}
                    className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded border transition-colors ${
                      active
                        ? "border-white bg-white/10"
                        : "border-[#1F1F1F] bg-[#000000] hover:border-[#2E2E2E]/40"
                    }`}
                    data-testid={`asset-${a.code}-btn`}
                  >
                    <CryptoIcon code={a.code} size={28} />
                    <span className={`text-xs font-semibold ${active ? "text-white" : "text-[#A1A1AA]"}`}>
                      {a.code}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Network (only for USDT with multiple) */}
          {asset.networks.length > 1 && (
            <div className="mb-6">
              <div className="text-[#A1A1AA] text-sm mb-2">Сеть</div>
              <div className="space-y-2">
                {asset.networks.map((n, idx) => {
                  const active = idx === activeNetworkIdx;
                  return (
                    <button
                      key={n.id}
                      type="button"
                      onClick={() => setActiveNetworkIdx(idx)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded border transition-colors ${
                        active
                          ? "border-white bg-white/10"
                          : "border-[#1F1F1F] bg-[#000000] hover:border-[#2E2E2E]/40"
                      }`}
                      data-testid={`network-${n.id}-btn`}
                    >
                      <div className="text-left">
                        <div className="text-white font-semibold text-sm">{n.label}</div>
                        <div className="text-[#71717A] text-xs">{n.subtitle}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[#A1A1AA] text-xs">Комиссия: {n.fee}</div>
                        <div className="text-[#71717A] text-[10px]">{n.time}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {asset.networks.length === 1 && (
            <div className="mb-6">
              <div className="text-[#A1A1AA] text-sm mb-2">Сеть</div>
              <div className="bg-[#000000] border border-[#1F1F1F] rounded px-4 py-3">
                <span className="text-[#EDEDED] font-bold">{network.label}</span>
                <span className="text-[#A1A1AA] ml-2">({network.subtitle})</span>
              </div>
            </div>
          )}

          {/* Wallet Address */}
          <div className="mb-6">
            <div className="text-[#A1A1AA] text-sm mb-2">Адрес кошелька</div>
            <div className="bg-[#000000] border border-[#1F1F1F] rounded px-4 py-3 flex items-center justify-between gap-4">
              <code className="text-white font-mono text-sm break-all">{address}</code>
              <button
                onClick={handleCopy}
                className="flex-shrink-0 p-2 bg-[#FF8A1E] hover:bg-[#E67A00] text-white rounded transition-colors"
                data-testid="copy-address-btn"
              >
                {copied ? <CheckCircle size={20} /> : <Copy size={20} />}
              </button>
            </div>
            <div className="mt-2 text-xs text-[#A1A1AA] flex items-start gap-1.5">
              <span className="text-[#EDEDED]">ℹ</span>
              <span>
                Данный адрес сгенерирован биржей <span className="text-white">Bybit</span> для вашего субаккаунта.
                Адрес <span className="text-[#EDEDED]">персональный</span> и <span className="text-[#EDEDED]">конфиденциальный</span>.
              </span>
            </div>
          </div>

          {/* Warning */}
          <div className="bg-white/10 border border-white/30 rounded-lg p-4">
            <div className="text-[#EDEDED] font-bold mb-2">Важно!</div>
            <ul className="text-[#A1A1AA] text-sm space-y-1">
              <li>• Отправляйте только <span className="text-white">{asset.code}</span> по сети <span className="text-white">{network.label}</span></li>
              <li>• Зачисление после <span className="text-white">подтверждения</span> сети</li>
              <li>• Адрес привязан к вашему аккаунту — <span className="text-white">не передавайте третьим лицам</span></li>
              {asset.code !== "USDT" && (
                <li>• Актив поступит в раздел <span className="text-white">«Мои активы»</span> в кошельке</li>
              )}
            </ul>
          </div>
        </div>

        <div className="text-center">
          <Link to="/withdrawal" className="text-[#A1A1AA] hover:text-[#EDEDED] transition-colors">
            Перейти к выводу средств →
          </Link>
        </div>
      </main>
    </div>
  );
};

export default Deposit;
